/**
 * Google Places loader — one shared script tag for the whole app.
 *
 * Address autocomplete previously existed only on the CloudSafe admin's property
 * form, so adding a building anywhere else meant typing the address by hand (and
 * getting no coordinates, so it never appeared on the map). This makes the loader
 * reusable so any "add a property" flow can offer the same lookup.
 */

const KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const SCRIPT_ID = 'gmap-places-script';
let pending = null;

// Google reports a bad/unbilled key asynchronously via window.gm_authFailure — the
// script loads fine and then every prediction is silently refused. Without this the
// field just looks broken, so track it and let the UI say so.
let authFailed = false;
const authWatchers = new Set();
export const placesAuthFailed = () => authFailed;
export function onPlacesAuthFailure(cb) {
  authWatchers.add(cb);
  return () => authWatchers.delete(cb);
}
const installAuthHook = () => {
  if (typeof window === 'undefined' || window.__csGmAuthHook) return;
  window.__csGmAuthHook = true;
  window.gm_authFailure = () => {
    authFailed = true;
    console.error('[googlePlaces] Google Maps rejected the API key — check the key and that billing is enabled on the Cloud project.');
    authWatchers.forEach((cb) => { try { cb(); } catch { /* ignore */ } });
  };
};

/** The Places dropdown renders in a portal on <body> — keep it above our modals. */
const ensurePacStyle = () => {
  if (document.getElementById('pac-z-fix')) return;
  const s = document.createElement('style');
  s.id = 'pac-z-fix';
  s.textContent = '.pac-container { z-index: 99999 !important; }';
  document.head.appendChild(s);
};

export const placesConfigured = () => !!KEY;

/**
 * Resolve once google.maps.places is usable. Rejects if there's no API key or the
 * script fails, so callers can fall back to a plain text input.
 */
export function loadPlaces() {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'));
  installAuthHook();
  if (window.google?.maps?.places) { ensurePacStyle(); return Promise.resolve(window.google.maps.places); }
  if (!KEY) return Promise.reject(new Error('REACT_APP_GOOGLE_MAPS_API_KEY is not set'));
  if (pending) return pending;

  ensurePacStyle();
  pending = new Promise((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID);
    const onReady = () => (window.google?.maps?.places
      ? resolve(window.google.maps.places)
      : reject(new Error('Places library unavailable')));
    if (existing) { existing.addEventListener('load', onReady); existing.addEventListener('error', reject); return; }
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = onReady;
    script.onerror = () => reject(new Error('Could not load Google Maps'));
    document.head.appendChild(script);
  });
  pending.catch(() => { pending = null; });   // let a later attempt retry
  return pending;
}

/**
 * Attach Places autocomplete to an existing <input>, so a form keeps its own
 * styling. Calls onPlace(parsed) when a suggestion is chosen.
 * Returns a cleanup function; resolves false if Places isn't available.
 */
export async function attachAutocomplete(input, onPlace, { country = 'gb' } = {}) {
  if (!input) return false;
  let places;
  try { places = await loadPlaces(); } catch { return false; }
  const ac = new places.Autocomplete(input, {
    fields: ['formatted_address', 'address_components', 'geometry', 'name'],
    ...(country ? { componentRestrictions: { country } } : {}),
  });
  ac.addListener('place_changed', () => onPlace(parsePlace(ac.getPlace() || {})));
  return () => { if (window.google?.maps?.event) window.google.maps.event.clearInstanceListeners(ac); };
}

/** Pull the bits we store from a Places result. */
export function parsePlace(place) {
  const comp = (type, short = false) => {
    const c = (place.address_components || []).find((x) => x.types.includes(type));
    return c ? (short ? c.short_name : c.long_name) : '';
  };
  const lat = place.geometry?.location?.lat?.();
  const lng = place.geometry?.location?.lng?.();
  return {
    address: place.formatted_address || place.name || '',
    postcode: comp('postal_code'),
    city: comp('postal_town') || comp('locality'),
    country: comp('country'),
    latitude: Number.isFinite(lat) ? Number(lat.toFixed(7)) : null,
    longitude: Number.isFinite(lng) ? Number(lng.toFixed(7)) : null,
    name: place.name || '',
  };
}
